import type { LambdaProcessingType } from '@/types';
import { logger, ValidationError, ProcessingError, NetworkError } from '@/utils/logger';
import { batchHistoryAPI } from '@/services/batchHistoryAPI';

interface AdminBatchResult {
  success: boolean;
  jobId: string;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  emailSent: boolean;
  message: string;
  csvDownloadUrl?: string;
  jsonDownloadUrl?: string;
  expiresAt?: string;
}


interface AdminProcessResponse {
  success: boolean;
  jobId: string;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  emailSent: boolean;
  csvDownloadUrl?: string;
  jsonDownloadUrl?: string;
  expiresAt?: string;
  error?: string;
}

export class AdminBatchService {
  private apiUrl: string;
  private adminApiKey: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_AWS_API_ENDPOINT;
    this.adminApiKey = import.meta.env.VITE_ADMIN_API_KEY || import.meta.env.VITE_AWS_API_KEY;

    if (!this.apiUrl) {
      throw new Error('VITE_AWS_API_ENDPOINT environment variable is required');
    }
    if (!this.adminApiKey) {
      throw new Error('VITE_ADMIN_API_KEY or VITE_AWS_API_KEY environment variable is required');
    }
  }

  async processInternalBatch(
    csvFile: File,
    operation: LambdaProcessingType,
    adminEmail: string
  ): Promise<AdminBatchResult> {
    if (!csvFile) {
      throw new ValidationError('CSV file is required', 'file');
    }
    if (!operation) {
      throw new ValidationError('Operation type is required', 'operation');
    }
    if (!adminEmail || !adminEmail.includes('@')) {
      throw new ValidationError('Valid admin email is required', 'adminEmail');
    }

    logger.info('🚀 Starting internal batch process...');
    logger.info(`File: ${csvFile.name} (${(csvFile.size/1024/1024).toFixed(2)}MB)`);
    logger.info(`Operation: ${operation}`);
    logger.info(`Admin Email: ${adminEmail}`);

    try {
      // Step 1: Get presigned upload URL
      logger.info('Step 1: Getting presigned upload URL...');
      const { uploadUrl, s3Key, s3Bucket } = await this.getUploadUrl(csvFile.name);
      logger.info('✅ Upload URL received');

      // Step 2: Upload CSV to S3
      logger.info('Step 2: Uploading CSV to S3...');
      await this.uploadFileToS3(uploadUrl, csvFile);
      logger.info('✅ File uploaded to S3');

      // Step 3: Submit batch job using new backend API
      logger.info('Step 3: Submitting batch job...');
      const processResult = await this.submitBatchJob(
        s3Bucket,
        s3Key,
        operation,
        'TestCompany' // Using TestCompany as specified in backend docs
      );
      logger.info('✅ Processing completed successfully');

      // Step 4: Create/Update batch record in database
      logger.info('Step 4: Storing batch record in database...');
      
      try {
        // First, try to create the batch record with the real jobId
        await batchHistoryAPI.createBatch(processResult.jobId, {
          filename: csvFile.name,
          operation: operation,
          totalRecords: processResult.totalRecords,
          adminEmail: adminEmail
        });
        
        // Then update it with completion data
        await batchHistoryAPI.updateBatch(processResult.jobId, {
          status: 'completed',
          successfulRecords: processResult.successfulRecords,
          failedRecords: processResult.failedRecords,
          csvDownloadUrl: processResult.csvDownloadUrl,
          jsonDownloadUrl: processResult.jsonDownloadUrl,
          emailSent: processResult.emailSent || false,
          expiresAt: processResult.expiresAt
        });
        
        logger.info('Batch record stored successfully in database:', { batchId: processResult.jobId });
      } catch (dbError) {
        // Log the error but don't fail the entire process since the batch completed successfully
        logger.error('Failed to store batch record in database (batch processing succeeded):', dbError);
      }

      return {
        success: true,
        jobId: processResult.jobId,
        totalRecords: processResult.totalRecords,
        successfulRecords: processResult.successfulRecords,
        failedRecords: processResult.failedRecords,
        emailSent: processResult.emailSent || false,
        csvDownloadUrl: processResult.csvDownloadUrl,
        jsonDownloadUrl: processResult.jsonDownloadUrl,
        expiresAt: processResult.expiresAt,
        message: `✅ Complete! ${processResult.successfulRecords}/${processResult.totalRecords} processed. Results available for download.`
      };

    } catch (error) {
      logger.error('❌ Internal batch error:', error);
      
      if (error instanceof ValidationError || error instanceof NetworkError) {
        throw error;
      }
      
      throw new ProcessingError(
        `Internal batch processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        undefined,
        'processing'
      );
    }
  }

  private async getUploadUrl(filename: string): Promise<{ uploadUrl: string; s3Key: string; s3Bucket: string }> {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-internal-api-key': this.adminApiKey
        },
        body: JSON.stringify({
          operation: 'get_upload_url',
          companyId: 'TestCompany',
          filename: filename
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new NetworkError(`Failed to get upload URL: ${response.statusText}. Response: ${errorText}`, response.status);
      }

      const data = await response.json();
      
      if (!data.uploadUrl || !data.s3Key) {
        throw new ProcessingError('Invalid upload URL response: missing required fields');
      }

      return {
        uploadUrl: data.uploadUrl,
        s3Key: data.s3Key,
        s3Bucket: data.s3Bucket || 'collectability-batch-uploads'
      };

    } catch (error) {
      if (error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      
      logger.error('Get upload URL error:', error);
      throw new NetworkError(`Failed to get upload URL: ${error instanceof Error ? error.message : 'Network error'}`, 0);
    }
  }

  private async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': 'text/csv' }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new NetworkError(`S3 file upload failed: ${response.statusText}. Response: ${errorText}`, response.status);
      }

    } catch (error) {
      if (error instanceof NetworkError) {
        throw error;
      }
      
      logger.error('S3 upload error:', error);
      throw new NetworkError(`Failed to upload file to S3: ${error instanceof Error ? error.message : 'Upload error'}`, 0);
    }
  }

  private async submitBatchJob(
    s3Bucket: string,
    s3Key: string,
    operation: LambdaProcessingType,
    companyId: string
  ): Promise<AdminProcessResponse> {
    try {
      const requestBody = {
        s3Bucket,
        s3Key,
        operation,
        companyId,
        internal: true  // KEY: This triggers internal flow
      };
      
      logger.info('Submit batch job request:', {
        url: this.apiUrl,
        headers: { 'Content-Type': 'application/json', 'x-internal-api-key': `${this.adminApiKey?.substring(0, 10)}...` },
        body: requestBody
      });

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-internal-api-key': this.adminApiKey
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new NetworkError(
          `Batch job submission failed: ${errorData.error || response.statusText}`,
          response.status
        );
      }

      const result = await response.json();
      
      logger.info('Batch job API response:', result);
      
      if (result.csvDownloadUrl) {
        logger.info('CSV Download URL:', result.csvDownloadUrl);
      }
      if (result.jsonDownloadUrl) {
        logger.info('JSON Download URL:', result.jsonDownloadUrl);
      }
      
      if (!result.success) {
        throw new ProcessingError(`Batch job failed: ${result.error || 'Unknown error'}`);
      }

      if (typeof result.jobId !== 'string') {
        throw new ProcessingError('Invalid batch job response: missing jobId');
      }

      return {
        success: true,
        jobId: result.jobId,
        totalRecords: result.totalRecords || 0,
        successfulRecords: result.successfulRecords || 0,
        failedRecords: result.failedRecords || 0,
        emailSent: result.emailSent || false,
        csvDownloadUrl: result.csvDownloadUrl,
        jsonDownloadUrl: result.jsonDownloadUrl,
        expiresAt: result.expiresAt
      };

    } catch (error) {
      if (error instanceof NetworkError || error instanceof ProcessingError) {
        throw error;
      }
      
      logger.error('Submit batch job error:', error);
      throw new ProcessingError(
        `Failed to submit batch job: ${error instanceof Error ? error.message : 'Processing error'}`
      );
    }
  }
}

export const adminBatchService = new AdminBatchService();